/**
 * TradingTerminal — main container using flexlayout-react.
 * Renders dockable, resizable, tabbable panels with pop-out support.
 */

import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  Actions,
  DockLocation,
  Layout,
  Model,
  type TabNode,
} from "flexlayout-react";
import "flexlayout-react/style/dark.css";

import { useTerminalStore } from "@/stores/terminal-store";
import type { LayoutPreset } from "@/stores/terminal-store";
import {
  ALL_PANEL_IDS,
  FEATURE_PANEL,
  getPreset,
  PANEL,
  PANEL_TITLES,
  type PanelId,
} from "../layout-presets";
import { TerminalTopBar } from "./TerminalTopBar";
import { TerminalErrorBoundary } from "./TerminalErrorBoundary";

// Panel components (lazy — start with placeholders, replace as we build)
import { PnlOverviewPanel } from "../panels/PnlOverviewPanel";
import { AgentPodPanel } from "../panels/AgentPodPanel";
import { AlphaAlertsPanel } from "../panels/AlphaAlertsPanel";
import { EventStreamPanel } from "../panels/EventStreamPanel";
import { NewsFeedPanel } from "../panels/NewsFeedPanel";
import { PositionsPanel } from "../panels/PositionsPanel";
import { TradeHistoryPanel } from "../panels/TradeHistoryPanel";
import { RiskDashboardPanel } from "../panels/RiskDashboardPanel";
import { ConfluencePanel } from "../panels/ConfluencePanel";
import { SystemHealthPanel } from "../panels/SystemHealthPanel";

// Feature panels shared with the page routes (imported via their barrels,
// never duplicated into ../panels) — add-panel-menu only, no preset slots.
import { CrossMarketPanel } from "@/features/cross-market";
import { LiveFeed } from "@/features/live-feed";
import { OraclePanel } from "@/features/oracle";
import { RiskPanel } from "@/features/risk";
import { SentinelPanel } from "@/features/sentinel";
import { TraderIntelPanel } from "@/features/trader-intel";

function buildModel(json: unknown): Model {
  return Model.fromJson(json as Parameters<typeof Model.fromJson>[0]);
}

/** Component keys of every tab currently in the layout (including border tabs). */
export function collectOpenPanels(model: Model): Set<string> {
  const open = new Set<string>();
  model.visitNodes((node) => {
    if (node.getType() === "tab") {
      const component = (node as TabNode).getComponent();
      if (component) {
        open.add(component);
      }
    }
  });
  return open;
}

function renderPanel(
  component: string | undefined,
  config: { agentId?: string } | undefined,
): ReactNode {
  switch (component) {
    case PANEL.PNL_OVERVIEW:
      return <PnlOverviewPanel />;
    case PANEL.AGENT_POD:
      return <AgentPodPanel agentId={config?.agentId} />;
    case PANEL.ALPHA_ALERTS:
      return <AlphaAlertsPanel />;
    case PANEL.EVENT_STREAM:
      return <EventStreamPanel />;
    case PANEL.NEWS_FEED:
      return <NewsFeedPanel />;
    case PANEL.POSITIONS:
      return <PositionsPanel />;
    case PANEL.TRADE_HISTORY:
      return <TradeHistoryPanel />;
    case PANEL.RISK_DASHBOARD:
      return <RiskDashboardPanel />;
    case PANEL.CONFLUENCE:
      return <ConfluencePanel />;
    case PANEL.SYSTEM_HEALTH:
      return <SystemHealthPanel />;
    case FEATURE_PANEL.CROSS_MARKET:
      return <CrossMarketPanel />;
    case FEATURE_PANEL.ORACLE:
      return <OraclePanel />;
    case FEATURE_PANEL.RISK_CAPS:
      return <RiskPanel />;
    case FEATURE_PANEL.SENTINEL:
      return <SentinelPanel />;
    case FEATURE_PANEL.WHALE_FLOW:
      return <TraderIntelPanel />;
    case FEATURE_PANEL.SIGNAL_FEED:
      return <LiveFeed />;
    default:
      return (
        <div className="flex h-full items-center justify-center text-zinc-500">
          Unknown panel: {component}
        </div>
      );
  }
}

/**
 * Panel factory — maps component key to React component. Every panel is
 * wrapped in its own error boundary so a render error in one tab degrades
 * to a single broken tile instead of unmounting the entire terminal
 * (Positions and kill-switch visibility must survive any panel bug).
 */
export function terminalPanelFactory(node: TabNode): ReactNode {
  const component = node.getComponent();
  const panelName =
    component && component in PANEL_TITLES
      ? PANEL_TITLES[component as PanelId]
      : (component ?? "panel");
  return (
    <TerminalErrorBoundary panelName={panelName}>
      {renderPanel(component, node.getConfig() as { agentId?: string } | undefined)}
    </TerminalErrorBoundary>
  );
}

export function TradingTerminal() {
  const { layoutJson, activePreset, setLayout } = useTerminalStore();
  const [state, setState] = useState(() => {
    if (layoutJson) {
      try {
        return { model: buildModel(layoutJson), recovered: false };
      } catch {
        // A corrupt or schema-stale persisted layout must never brick the
        // terminal: fall back to the preset. The store cleanup happens in an
        // effect below — never a render-phase side effect.
        return { model: buildModel(getPreset(activePreset)), recovered: true };
      }
    }
    return { model: buildModel(getPreset(activePreset)), recovered: false };
  });
  const model = state.model;
  const setModel = useCallback((next: Model) => {
    setState({ model: next, recovered: false });
  }, []);

  // After recovering from a corrupt persisted layout, replace the bad JSON so
  // the next reload loads cleanly instead of depending on the catch again.
  useEffect(() => {
    if (state.recovered) {
      setState((s) => ({ ...s, recovered: false }));
      setLayout(getPreset(activePreset) as unknown as Record<string, unknown>);
    }
  }, [state.recovered, activePreset, setLayout]);

  // Persist on model change
  const handleModelChange = useCallback(
    (model: Model) => {
      setLayout(model.toJson() as unknown as Record<string, unknown>);
    },
    [setLayout],
  );

  // Reset to a preset
  const handleResetLayout = useCallback(
    (preset: LayoutPreset) => {
      const json = getPreset(preset);
      setModel(buildModel(json));
      setLayout(json as unknown as Record<string, unknown>);
    },
    [setLayout, setModel],
  );

  // Re-add a closed panel into the active (or first) tabset — the only
  // recovery path for a closed tab that does not wipe the whole layout.
  const handleAddPanel = useCallback(
    (panelId: PanelId) => {
      const target = model.getActiveTabset() ?? model.getFirstTabSet();
      // flexlayout 0.10 types getFirstTabSet() as possibly-undefined: a
      // layout with zero tabsets has no anchor to add into — nothing to do.
      if (!target) {
        return;
      }
      model.doAction(
        Actions.addNode(
          { type: "tab", name: PANEL_TITLES[panelId], component: panelId },
          target.getId(),
          DockLocation.CENTER,
          -1,
        ),
      );
      // doAction mutates the model in place; persist and re-render explicitly
      // so the missing-panel list never depends on the Layout's own
      // change-listener round-trip.
      setLayout(model.toJson() as unknown as Record<string, unknown>);
    },
    [model, setLayout],
  );

  // Sync when activePreset changes externally
  useEffect(() => {
    if (!layoutJson) {
      setModel(buildModel(getPreset(activePreset)));
    }
  }, [activePreset, layoutJson, setModel]);

  // Recomputed every render: model changes funnel through setLayout (store
  // update -> re-render), so this stays current.
  const openPanels = collectOpenPanels(model);
  const missingPanels = ALL_PANEL_IDS.filter((id) => !openPanels.has(id));

  return (
    <div className="flex h-[100dvh] w-full flex-col bg-zinc-950 text-white overflow-hidden">
      <TerminalTopBar
        onResetLayout={handleResetLayout}
        missingPanels={missingPanels}
        onAddPanel={handleAddPanel}
      />
      <div className="relative flex-1">
        <Layout
          model={model}
          factory={terminalPanelFactory}
          onModelChange={handleModelChange}
          realtimeResize
        />
      </div>
    </div>
  );
}
